/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { mimeTypeToExtension } from "utils/getFileMIME";
import openToast from "utils/openToast";
import getFileSize from "utils/getFileSize";

import {
  getCloudinaryConfig,
  CLOUDINARY_CLOUD_NAME,
  CLOUDINARY_UPLOAD_PRESET,
} from "utils/cloudinary";

const uploadCloudinary = async (
  payload,
  {
    onUploadStart,
    onUpload,
    onError,
    allowedFileTypes = [],
    isEager = false,
    folderName,
    maxSize = 10,
    uploadPreset = CLOUDINARY_UPLOAD_PRESET,
    middleware,
    cloudinaryType = "auto",
  },
  width,
  _height,
) => {
  const payloadArray = Array.from(payload);
  if (onUploadStart && !!payloadArray.length) onUploadStart();
  const newImages = [];
  let isAllSuccess = true;
  for (const file of payloadArray) {
    const isBase64 = typeof file === "string" && file.startsWith("data:");
    const mimeType = isBase64 ? file.split(";")[0].split(":")[1] : file.type;

    if (allowedFileTypes && allowedFileTypes.length > 0) {
      // "image/jpeg" => jpeg, "application/pdf" => pdf
      const fileExtension = mimeTypeToExtension(mimeType);
      // ["image/*"]...etc
      const isFormatsHasAsterisk =
        allowedFileTypes.filter((f) => isFormatIncludesAllTypes(f, mimeType))
          .length > 0;

      const isAllowed =
        allowedFileTypes.includes(fileExtension) ||
        allowedFileTypes.includes(mimeType) ||
        isFormatsHasAsterisk ||
        isBase64;
      //base64 string
      if (!isAllowed) {
        const allowedFormats = allowedFileTypes.join(" / ");
        openToast(`Only (${allowedFormats}) are allowed`, "error");
        if (onError) onError();
        isAllSuccess = false;
        continue;
      }
    }

    if (!isBase64) {
      const fileSize = getFileSize(file.size);
      if (fileSize > maxSize) {
        openToast(`File size must be less than ${maxSize}MB`, "error");
        if (onError) onError();
        isAllSuccess = false;
        continue;
      }
    }

    const { type, uploadPreset: finalUploadPreset } = getCloudinaryConfig(
      file,
      uploadPreset,
      cloudinaryType,
    );

    const formData = new FormData();

    formData.append("file", file);
    formData.append("cloudName", CLOUDINARY_CLOUD_NAME);
    formData.append("folder", folderName);
    formData.append("upload_preset", finalUploadPreset);

    const DEFAULT_WIDTH = 800;
    // const DEFAULT_HEIGHT = 150;

    const isWidthAndHeightValid = !isNaN(Number(width)) && (width ?? 0) > 0;
    // !isNaN(Number(height)) &&
    // (height ?? 0) > 0;

    const BASE_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${type}/upload`;

    const DEFAULT_RESIZE_URL = `/upload/w_${DEFAULT_WIDTH}/`;

    const RESIZE_URL = `/upload/w_${width}/`;

    const resizedUrl = (url) => {
      if (mimeType?.startsWith("video/")) {
        return url;
      }

      if (isWidthAndHeightValid) {
        return url.replace("/upload/", RESIZE_URL);
      }

      return url.replace("/upload/", DEFAULT_RESIZE_URL);
    };

    const req = await fetch(BASE_URL, {
      method: "post",
      body: formData,
    });

    if (!req.ok) {
      if (onError) onError();
      openToast("An error occurred while uploading file", "error");
      continue;
    }

    if (req.ok) {
      const body = await req.json();
      let image;
      if (
        isEager &&
        body.hasOwnProperty("eager") &&
        body.eager.length > 0 &&
        body.eager[0].hasOwnProperty("secure_url")
      ) {
        const { secure_url, bytes } = body.eager[0];

        image = getTransformedUrlBySize(bytes, secure_url, body.secure_url);
      } else if (middleware) {
        const payload = middleware(body);
        if (payload.hasOwnProperty("image")) {
          image = payload.image;
        } else {
          image = body.secure_url;
        }
      } else {
        image = body.secure_url;
      }

      newImages.push({
        id: body.asset_id,
        img: resizedUrl(image),
        originalFile: file,
      });
    }
  }

  if (onUpload) onUpload(newImages, isAllSuccess);
};

const isFormatIncludesAllTypes = (format, fileType) => {
  const [type, asterisk] = format.split("/");
  if (!fileType) return false;
  return fileType.includes(type) && asterisk === "*";
};

export default uploadCloudinary;

const getTransformedUrlBySize = (size, eagerURL, originalURL) => {
  const SIZE_TRANSFORMATIONS = {
    "c_scale,q_50,w_1200": (size) => size > 1 && size <= 2, // 1MB - 2MB
    "c_scale,q_60,w_1400": (size) => size > 0.5 && size <= 1, // 500KB - 1MB
  };

  const fileSizeInMB = getFileSize(size);

  const sizeTransformation = Object.keys(SIZE_TRANSFORMATIONS).find((key) =>
    SIZE_TRANSFORMATIONS[key](fileSizeInMB),
  );
  // it's less than 500kb...so we don't need to transform it.
  if (!sizeTransformation) return eagerURL;
  const injectTransformation = (url) => {
    const [firstPart, secondPart] = url.split("/upload/");
    return `${firstPart}/upload/${sizeTransformation}/${secondPart}`;
  };

  return injectTransformation(originalURL);
};
